import { useState, useEffect, useCallback, useMemo } from 'react';
import type { DisplaySourceManager } from '../managers/DisplaySourceManager';
import constants from '@playcastdotio/GlobalConfig';

export type BeaconSourceChoice = {
  label: string;
  value: string;
};

export type BeaconSource = {
  label: 'Desktop' | 'Cameras' | 'Window Capture';
  choices: BeaconSourceChoice[];
};

export type DisplaySourceHookResult = {
  sources: BeaconSource[];
  selectedDevice: string;
  selectedLabel: string;
  onSelectDesktop: () => void;
  onSelectSurfMode: () => void;
  onSelectDesktopCapture: () => void;
  onSelectCamera: (deviceId: string) => void;
  onSelectWindowCapture: (processId: number) => void;
  onRefreshApps: () => void;
};

export function useDisplaySource(manager: DisplaySourceManager): DisplaySourceHookResult {
  const store = manager.getStore();
  const [state, setState] = useState(store.currentState);

  useEffect(() => {
    setState(store.currentState);
    const id = store.addListener((s) => setState(s));
    return () => store.removeListener(id);
  }, [store]);

  const sources = useMemo((): BeaconSource[] => {
    const desktopChoices: BeaconSourceChoice[] = [
      { label: 'Display', value: 'display' },
      { label: 'Surf Desktop', value: 'followMouse' },
    ];

    if (constants.ENVIRONMENT === 'development') {
      desktopChoices.push({ label: 'Desktop Capture', value: 'desktopCapture' });
    }

    return [
      { label: 'Desktop', choices: desktopChoices },
      {
        label: 'Window Capture',
        choices: state.runningApps.map((app) => ({
          label: app.name,
          value: app.processId.toString(),
        })),
      },
      {
        label: 'Cameras',
        choices: state.cameras.map((cam) => ({
          label: cam.displayName,
          value: cam.deviceId,
        })),
      },
    ];
  }, [state.cameras, state.runningApps]);

  const selectedLabel = useMemo((): string => {
    if (state.windowCapturePid) {
      const app = state.runningApps.find((a) => a.processId === state.windowCapturePid);
      if (app) return app.name;
    }

    const allChoices = sources.flatMap((s) => s.choices);
    return allChoices.find((c) => c.value === state.selectedDevice)?.label ?? 'Source';
  }, [state.selectedDevice, state.windowCapturePid, state.runningApps, sources]);

  const onSelectDesktop = useCallback(() => manager.selectDesktopDisplay(), [manager]);
  const onSelectSurfMode = useCallback(() => manager.selectSurfMode(), [manager]);
  const onSelectDesktopCapture = useCallback(() => manager.selectDesktopCapture(), [manager]);
  const onSelectCamera = useCallback((deviceId: string) => manager.selectCamera(deviceId), [manager]);
  const onSelectWindowCapture = useCallback((pid: number) => manager.selectWindowCapture(pid), [manager]);
  const onRefreshApps = useCallback(() => manager.refreshRunningApps(), [manager]);

  return {
    sources,
    selectedDevice: state.selectedDevice,
    selectedLabel,
    onSelectDesktop,
    onSelectSurfMode,
    onSelectDesktopCapture,
    onSelectCamera,
    onSelectWindowCapture,
    onRefreshApps,
  };
}
